import { Button, useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { InsuranceDrpSettings, PartType, RekeySettings } from '../../../../hooks/useGetSettings';
import { useGetLocations } from '../../../../hooks/useGetLocations';
import { useUpdateSettings } from '../../../../hooks/useUpdateSettings';
import {
    useRelatedInsurance,
    useSearchInsurance,
    type InsuranceSearchResult,
} from '../../../../hooks/useSearchInsurance';
import { SettingsFlow, useSettingsDetailCrumbs } from '../../../../components/settings/primitives';
import {
    fromPriceMatchDraft,
    isPriceMatchDraftValid,
    toPriceMatchDraft,
    type PriceMatchDraftEntry,
} from '../../../../components/settings/priceMatchCriteria';
import PriceMatchOrderEditor from '../priceMatch/PriceMatchOrderEditor';
import {
    apuEntryForCarrier,
    carriersForDrpApu,
    setApuOverrideForCarriers,
} from '../../../../components/settings/apuOverrideDraft';
import {
    groupParentLabel,
    mergeTargetFor,
} from '../../../../components/settings/insuranceRuleSets';
import DrpCarrierStep from './DrpCarrierStep';
import DrpOptionsStep from './DrpOptionsStep';
import {
    buildDrpDraft,
    buildInsuranceList,
    savedLocationIds,
    seedSubModes,
    type DrpDraft,
} from './drpDraft';

export interface DrpDetailSectionProps {
    /** An insurer id, or 'new' for the add case. */
    drpId: string;
    insurance: Array<InsuranceDrpSettings>;
    /**
     * The re-key settings this carrier's APU override is stored on. The list
     * shape is unchanged: one `{ insuranceCompanies, partType }` entry.
     */
    rekeySettings?: RekeySettings;
    managementSystem?: 'ccc' | 'nex';
    refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<unknown, Error>>;
}

type StepId = 'carrier' | 'options';

/**
 * Add or edit one DRP carrier, full screen and step by step: who the carrier is
 * and where it applies, then how PartBay prices and orders for it. Replaces the
 * two-pane DRP modal, so the same screens serve adding and editing.
 *
 * Saving writes the whole insurance array through POST /settings/update, which
 * is how this endpoint works, so the draft is merged into the current list here.
 * A carrier and the sub companies riding along on its settings are one row in
 * the DRP table: each insurer is still stored on its own, because that is what
 * the rest of the platform looks a carrier up by, but the sub companies point at
 * their parent instead of owning rules. Branching one gives it rules — and a row
 * — of its own.
 */
const DrpDetailSection = ({
    drpId,
    insurance,
    rekeySettings,
    managementSystem,
    refetch,
}: DrpDetailSectionProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const updateSettings = useUpdateSettings();
    const { data: locationsData } = useGetLocations();

    const isAddMode = drpId === 'new';
    const carrier = isAddMode
        ? undefined
        : insurance.find((candidate) => candidate.insurerId === drpId);

    const [stepId, setStepId] = useState<StepId>('carrier');
    const [draft, setDraft] = useState<DrpDraft>(() => buildDrpDraft(carrier));
    // The custom order, while its sub-view of the Options step is open. Null
    // means closed; the saved order lives on the draft until then.
    const [rankingDrafts, setRankingDrafts] = useState<Array<PriceMatchDraftEntry> | null>(null);
    // Null means this carrier uses the organization APU default. Seeded from the
    // existing override when the carrier is known, then only rewritten on save.
    const [apuPartType, setApuPartType] = useState<PartType | null>(null);
    const apuSeededFor = useRef<string | undefined>(undefined);

    // Real shops only; the synthetic 'all' entry is the table's header row.
    const locations = useMemo(
        () => locationsData?.locations?.filter((location) => location._id !== 'all') ?? [],
        [locationsData],
    );
    const allLocationIds = useMemo(
        () => locations.map((location) => location._id as string),
        [locations],
    );

    // Locations arrive from their own query, so the draft is seeded once they
    // land rather than on mount, and only while it has none — otherwise a
    // refetch would wipe the user's picks.
    const seededLocationsRef = useRef(false);
    useEffect(() => {
        if (seededLocationsRef.current || allLocationIds.length === 0) return;
        seededLocationsRef.current = true;
        setDraft((prev) => ({ ...prev, locationIds: savedLocationIds(carrier, allLocationIds) }));
    }, [allLocationIds, carrier]);

    // An unknown insurer id has nothing to edit, so fall back to the list.
    useEffect(() => {
        if (!isAddMode && !carrier) navigate('/settings/drp', { replace: true });
    }, [isAddMode, carrier, navigate]);

    // Carriers saved before groups were tracked have no group on them, so the
    // group is recovered from the insurer's own name — one lookup, and only for
    // those rows; saving stores the group from then on.
    const needsGroupLookup = !!carrier && !draft.groupId;
    const { data: nameSearch } = useSearchInsurance(needsGroupLookup ? draft.insurerName : '');
    useEffect(() => {
        if (!needsGroupLookup) return;
        const match = nameSearch?.insuranceCompanies?.find(
            (company) => company._id === draft.insurerId,
        );
        if (match?.groupId) setDraft((prev) => ({ ...prev, groupId: match.groupId }));
    }, [needsGroupLookup, nameSearch, draft.insurerId]);

    useEffect(() => {
        if (!rekeySettings || !draft.insurerId) return;
        if (apuSeededFor.current === draft.insurerId) return;
        apuSeededFor.current = draft.insurerId;
        const custom = rekeySettings.apuMaximization?.custom ?? [];
        setApuPartType(apuEntryForCarrier(custom, draft.insurerId)?.partType ?? null);
    }, [draft.insurerId, rekeySettings]);

    const { data: relatedData } = useRelatedInsurance(draft.groupId);
    const relatedCompanies = useMemo(() => {
        if (!relatedData?.relatedCompanies || !draft.insurerId) return [];
        return relatedData.relatedCompanies.filter((company) => company._id !== draft.insurerId);
    }, [relatedData, draft.insurerId]);

    /**
     * A group mate that already owns rules, offered as something to merge into.
     * Two entries from one group are nearly always the same DRP relationship,
     * whether that shows up while adding the second name or on a carrier
     * configured separately long before groups were tracked.
     */
    /**
     * The group's own name, which only the settings endpoint knows. Any
     * configured member carries it, so one of them answers for the whole group.
     */
    const mergeTarget = useMemo(
        () =>
            mergeTargetFor(
                {
                    insurerId: draft.insurerId,
                    insurerName: draft.insurerName,
                    groupId: draft.groupId,
                },
                insurance,
            ),
        [draft.insurerId, draft.insurerName, draft.groupId, insurance],
    );

    /** The group parent this carrier sits under, for the card's description. */
    const parentLabel = useMemo(
        () =>
            groupParentLabel(
                {
                    insurerId: draft.insurerId,
                    insurerName: draft.insurerName,
                    groupId: draft.groupId,
                    parentInsurerName: draft.parentInsurerName,
                },
                insurance,
            ),
        [draft.insurerId, draft.insurerName, draft.groupId, draft.parentInsurerName, insurance],
    );

    // Sub companies default to riding along on a new carrier's settings: a group
    // is normally taken on whole. Seeded once per group so a refetch of the
    // group or the settings never overwrites the user's choices.
    const seededGroupRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!draft.groupId || relatedCompanies.length === 0) return;
        if (seededGroupRef.current === draft.groupId) return;
        seededGroupRef.current = draft.groupId;
        setDraft((prev) => ({
            ...prev,
            subModes: seedSubModes(relatedCompanies, insurance, prev.insurerId, isAddMode),
        }));
    }, [draft.groupId, relatedCompanies, insurance, isAddMode]);

    /**
     * One row per group mate, with what happens to it on save. A mate already
     * configured under someone else says whose settings it is on, so this screen
     * never claims to own a carrier it will not touch.
     */
    const subCompanyRows = useMemo(
        () =>
            relatedCompanies.map((company) => ({
                insurerId: company._id,
                name: company.name,
                mode: draft.subModes[company._id] ?? 'none',
                followsName: undefined,
            })),
        [relatedCompanies, draft.subModes],
    );

    /** Sub companies currently set to ride along on the carrier's rules. */
    const sharedSubCompanyNames = useMemo(
        () =>
            relatedCompanies
                .filter((company) => draft.subModes[company._id] === 'same')
                .map((company) => company.name),
        [relatedCompanies, draft.subModes],
    );

    useSettingsDetailCrumbs([
        { label: isAddMode ? 'Add carrier' : draft.insurerName || 'Carrier' },
        ...(rankingDrafts ? [{ label: 'Custom price match order' }] : []),
    ]);

    const closeFlow = () => navigate('/settings/drp');

    const hasCarrier = draft.insurerName.trim().length > 0;
    // Following a parent needs no locations of its own: it inherits them.
    const isJoining = !!draft.joinParentId;
    const isSaveDisabled = isJoining
        ? !hasCarrier
        : !hasCarrier || draft.locationIds.length === 0;

    const handleSelectCarrier = (company: InsuranceSearchResult) => {
        seededGroupRef.current = undefined;
        setDraft((prev) => ({
            ...prev,
            insurerId: company._id,
            insurerName: company.name,
            groupId: company.groupId || undefined,
            subModes: {},
            joinParentId: undefined,
            joinParentName: undefined,
            // Carriers that require a marketplace comparison start with it on.
            sourcePartsRequired: !!company.sourcePartsRequired,
            partstraderRequired: !!company.partstraderRequired,
            priceMatchCCC: company.sourcePartsRequired ? true : prev.priceMatchCCC,
            priceMatchPartstrader: company.partstraderRequired ? true : prev.priceMatchPartstrader,
        }));
    };

    const handleClearCarrier = () => {
        seededGroupRef.current = undefined;
        setDraft((prev) => ({
            ...prev,
            insurerId: '',
            insurerName: '',
            groupId: undefined,
            subModes: {},
            joinParentId: undefined,
            joinParentName: undefined,
            sourcePartsRequired: false,
            partstraderRequired: false,
        }));
    };

    const handleJoinParent = (parent: { insurerId: string; insurerName: string }) => {
        setStepId('carrier');
        setDraft((prev) => ({
            ...prev,
            joinParentId: parent.insurerId,
            joinParentName: parent.insurerName,
        }));
    };

    const handleCancelJoin = () => {
        setDraft((prev) => ({ ...prev, joinParentId: undefined, joinParentName: undefined }));
    };

    const handleSave = () => {
        if (!hasCarrier || !draft.insurerId) return;

        // Adding a carrier that already owns rules would start a second set for
        // the same insurer; its own screen is where those rules are changed.
        if (!carrier && insurance.some((item) => item.insurerId === draft.insurerId)) {
            toast({
                title: 'Carrier already added',
                description: `${draft.insurerName} is already a DRP partner. Open it from the DRP list to change its settings or its sub companies.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        const nextList = buildInsuranceList({ insurance, draft, relatedCompanies, locations });
        const currentApu = rekeySettings?.apuMaximization ?? { default: 'oem' as const, custom: [] };
        const storedApuPartType = draft.insurerId
            ? apuEntryForCarrier(currentApu.custom, draft.insurerId)?.partType ?? null
            : null;
        const apuChanged = !!rekeySettings && storedApuPartType !== apuPartType;
        const nextRekeySettings: RekeySettings | undefined =
            apuChanged && rekeySettings
                ? {
                    ...rekeySettings,
                    apuMaximization: {
                        ...currentApu,
                        custom: setApuOverrideForCarriers(
                            currentApu.custom,
                            carriersForDrpApu(
                                { _id: draft.insurerId, name: draft.insurerName },
                                relatedCompanies,
                                draft.subModes,
                            ),
                            apuPartType,
                        ),
                    },
                }
                : undefined;
        const sharedCount = relatedCompanies.filter(
            (company) => draft.subModes[company._id] === 'same',
        ).length;
        const branchedCount = relatedCompanies.filter(
            (company) => draft.subModes[company._id] === 'separate',
        ).length;

        updateSettings.mutate(
            {
                insurance: nextList,
                ...(nextRekeySettings ? { rekeySettings: nextRekeySettings } : {}),
            },
            {
                onSuccess: () => {
                    const parts = [
                        sharedCount > 0
                            ? `${sharedCount} sub ${sharedCount === 1 ? 'company' : 'companies'} on the same settings`
                            : '',
                        branchedCount > 0
                            ? `${branchedCount} branched onto ${branchedCount === 1 ? 'its own rules' : 'their own rules'}`
                            : '',
                    ].filter(Boolean);
                    toast({
                        title: carrier ? 'DRP partner updated' : 'DRP partner added',
                        description: draft.joinParentId
                            ? `${draft.insurerName} now follows ${draft.joinParentName}'s settings.`
                            : parts.length > 0
                                ? `${draft.insurerName} saved with ${parts.join(' and ')}.`
                                : `${draft.insurerName} has been saved.`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch?.();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: carrier ? 'Could not update DRP partner' : 'Could not add DRP partner',
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const handleRemove = () => {
        if (!carrier) return;
        // The sub companies riding on this carrier's rules only existed under
        // it, so they go with it rather than being left without a parent.
        const removedIds = new Set([carrier.insurerId]);
        updateSettings.mutate(
            { insurance: insurance.filter((item) => !removedIds.has(item.insurerId)) },
            {
                onSuccess: () => {
                    const subCount = removedIds.size - 1;
                    toast({
                        title: 'DRP partner removed',
                        description: subCount
                            ? `${carrier.insurerName} and ${subCount} sub ${subCount === 1 ? 'company' : 'companies'} have been removed.`
                            : `${carrier.insurerName} has been removed.`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                    refetch?.();
                    closeFlow();
                },
                onError: (error) => {
                    toast({
                        title: 'Could not remove DRP partner',
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const openRanking = () =>
        setRankingDrafts(draft.priceMatchCustomRanking.map(toPriceMatchDraft));

    /**
     * Leaves the order's sub-view, keeping the cards that carry a criterion. A
     * blank card would match everything, and an order with nothing in it is the
     * option being off.
     */
    const closeRanking = () => {
        const ranking = (rankingDrafts ?? []).filter(isPriceMatchDraftValid).map(fromPriceMatchDraft);
        setDraft((prev) => {
            const priceMatchCustom = ranking.length > 0;
            const next = {
                ...prev,
                priceMatchCustomRanking: ranking,
                priceMatchCustom,
            };
            const othersOff = !next.priceMatchCCC && !next.priceMatchPartstrader && !priceMatchCustom;
            return othersOff ? { ...next, priceMatchEstimate: true } : next;
        });
        setRankingDrafts(null);
    };

    if (!isAddMode && !carrier) return null;

    return (
        <>
            <SettingsFlow
                steps={[
                    { id: 'carrier', label: 'Carrier' },
                    // Nothing to configure until there is a carrier to
                    // configure — and nothing at all when it follows a parent.
                    { id: 'options', label: 'Options', isDisabled: !hasCarrier || isJoining },
                ]}
                activeStepId={stepId}
                onStepSelect={(id) => {
                    if (id === 'options' && (!hasCarrier || isJoining)) return;
                    setStepId(id as StepId);
                }}
                onCancel={closeFlow}
                onBack={
                    rankingDrafts
                        ? closeRanking
                        : stepId === 'options'
                            ? () => setStepId('carrier')
                            : undefined
                }
                onNext={isJoining ? undefined : () => setStepId('options')}
                isNextDisabled={!hasCarrier || draft.locationIds.length === 0}
                onSave={handleSave}
                saveLabel={isAddMode ? 'Add Carrier' : 'Save Carrier'}
                isSaveDisabled={isSaveDisabled}
                isSaving={updateSettings.isPending}
                // The order is a sub-view of the Options step, so its own Done
                // replaces Save until the user is back on the step itself.
                primaryAction={
                    rankingDrafts ? { label: 'Done', onClick: closeRanking } : undefined
                }
                actions={
                    carrier ? (
                        <Button
                            size="sm"
                            variant="outline"
                            colorScheme="red"
                            onClick={handleRemove}
                            isDisabled={updateSettings.isPending}
                        >
                            Remove Carrier
                        </Button>
                    ) : undefined
                }
            >
                {rankingDrafts ? (
                    <PriceMatchOrderEditor
                        entries={rankingDrafts}
                        onChange={setRankingDrafts}
                        rightTitle="Custom price match order"
                        rightDescription={`Tried top to bottom for ${draft.insurerName || 'this carrier'}. Drag a card to change its priority, or edit it here.`}
                    />
                ) : stepId === 'carrier' ? (
                    <DrpCarrierStep
                        draft={draft}
                        onChange={setDraft}
                        isEditMode={!!carrier}
                        locations={locations}
                        subCompanyRows={subCompanyRows}
                        mergeTarget={mergeTarget}
                        parentLabel={parentLabel}
                        onSelectCarrier={handleSelectCarrier}
                        onClearCarrier={handleClearCarrier}
                        onJoinParent={handleJoinParent}
                        onCancelJoin={handleCancelJoin}
                    />
                ) : (
                    <DrpOptionsStep
                        draft={draft}
                        onChange={setDraft}
                        onEditCustomRanking={openRanking}
                        sharedWith={sharedSubCompanyNames}
                        apu={
                            rekeySettings
                                ? {
                                    defaultPartType: rekeySettings.apuMaximization?.default ?? 'oem',
                                    partType: apuPartType,
                                    disabled: !rekeySettings.shouldRekey,
                                    notice:
                                        managementSystem === 'ccc' && rekeySettings.createManualPO
                                            ? 'inactive'
                                            : managementSystem === 'ccc'
                                                ? 'ccc'
                                                : 'california',
                                    onChange: setApuPartType,
                                }
                                : undefined
                        }
                    />
                )}
            </SettingsFlow>
        </>
    );
};

export default DrpDetailSection;
